import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

export default function CER() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [cers, setCers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ published_literature_summary: "", equivalent_devices_summary: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data);
      if (data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("Load projects:", e?.response?.status));
  }, [authLoading]);

  const load = useCallback(() => {
    if (!pid) return;
    api.get("/clinical/cer", { params: { project_id: pid } })
      .then(({ data }) => setCers(data))
      .catch((e) => logger.warn("CER load:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/clinical/cer/draft", { project_id: pid, ...form });
      toast.success("CER draft generated.");
      setOpen(false);
      setForm({ published_literature_summary: "", equivalent_devices_summary: "" });
      setSelected(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed."); }
    finally { setBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="vq-overline mb-2 flex items-center gap-2"><FileText className="w-3 h-3" /> Clinical Evaluation Report</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">CER Generator</h1>
            <p className="text-slate-600 mt-2 max-w-3xl">
              MDCG 2020-13 aligned scaffold from device data, risk register and complaints. Every section requires
              qualified clinical evaluator authorship and signature per ISO 14971 §8.
            </p>
          </div>
          <Button data-testid="new-cer-btn" onClick={() => setOpen(true)} disabled={!pid}><Sparkles className="w-4 h-4 mr-1" /> Draft CER</Button>
        </div>

        <div className="mb-4 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="cer-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="vq-card overflow-hidden">
            <div className="p-3 border-b bg-slate-50 font-display font-bold text-sm">Draft CERs</div>
            <table className="w-full text-sm">
              <tbody data-testid="cer-list">
                {cers.length === 0 && <tr><td className="text-center px-3 py-6 text-slate-400">No drafts yet.</td></tr>}
                {cers.map((c) => (
                  <tr key={c.cer_id} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${selected?.cer_id === c.cer_id ? "bg-blue-50" : ""}`}
                      onClick={() => setSelected(c)} data-testid={`cer-row-${c.cer_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.cer_id}</td>
                    <td className="px-3 py-2">v{c.version} · {c.state}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="vq-card p-4" data-testid="cer-detail-panel">
            {!selected ? (
              <div className="text-center text-slate-400 text-sm py-12">Select a draft on the left to view details.</div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-lg">{selected.device_name}</div>
                    <div className="text-xs font-mono text-slate-500">{selected.cer_id} · v{selected.version}</div>
                  </div>
                </div>
                <div className="vq-card p-3 border-l-4 border-l-amber-500 bg-amber-50 flex items-start gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900">{selected.disclaimer}</div>
                </div>
                <div className="space-y-3 text-sm">
                  {Object.entries(selected.sections || {}).map(([k, v]) => (
                    <div key={k}>
                      <div className="font-display font-bold text-xs uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                      <div className="mt-1 text-slate-700 whitespace-pre-wrap">
                        {typeof v === "string" ? v : <pre className="text-xs bg-slate-50 p-2 rounded">{JSON.stringify(v, null, 2)}</pre>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Draft a new CER</DialogTitle>
              <DialogDescription>Optional inputs enrich the scaffold. Empty fields leave placeholders for the evaluator.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Published literature summary (optional)</Label>
                <Textarea data-testid="cer-lit" rows={5} value={form.published_literature_summary} onChange={(e) => setForm({ ...form, published_literature_summary: e.target.value })} placeholder="Summary of published clinical literature reviewed…" />
              </div>
              <div>
                <Label>Equivalent devices summary (optional)</Label>
                <Textarea data-testid="cer-equiv" rows={4} value={form.equivalent_devices_summary} onChange={(e) => setForm({ ...form, equivalent_devices_summary: e.target.value })} placeholder="If claiming equivalence, list the equivalent devices and justification…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="cer-submit" onClick={draft} disabled={busy}>{busy ? "Drafting…" : "Draft CER"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
